import { useState, useCallback } from "react";
import { useAuthenticatedFetchWithWorkspace } from "../../Keycloak/useAuthenticatedFetchWithWorkspace";

export interface PersonWaitingUpdateDetails {
    COMM_POSTAL_ADDRESS?: boolean;
    COMM_EMAIL?: boolean;
    COMM_CELL_PHONE?: boolean;
    MOVE_ENTITY?: boolean;
    PER_ACCOUNT_MISC_MOVE?: boolean;
    PER_PERSON_IDENTIFIER?: boolean;
    PER_PERSON?: boolean;
    FORM_ANSWERED_FORM?: boolean;
    TRA_MAN_TRANSAC?: boolean;
    [key: string]: boolean | undefined;
}

export interface PersonWaitingUpdate {
    hasWaitingUpdate: boolean;
    details: PersonWaitingUpdateDetails;
}

const useGetPersonWaitingUpdate = () => {
    const { authFetch, isReady } = useAuthenticatedFetchWithWorkspace();
    const [waitingUpdate, setWaitingUpdate] = useState<PersonWaitingUpdate | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    const getPersonWaitingUpdate = useCallback(
        async (personId: string | number): Promise<void> => {
            if (!isReady) return;
            setIsLoading(true);
            setError("");
            try {
                const result = await authFetch(
                    `/getPersonWaitingUpdate?person_id=${encodeURIComponent(String(personId))}`,
                    { method: "GET" }
                );
                if (result && typeof result === "object" && !Array.isArray(result)) {
                    const data = result as Record<string, unknown>;
                    setWaitingUpdate({
                        hasWaitingUpdate: Boolean(data.hasWaitingUpdate),
                        details: (data.details as PersonWaitingUpdateDetails) ?? {},
                    });
                } else {
                    setWaitingUpdate(null);
                }
            } catch (err: unknown) {
                setError(err instanceof Error ? err.message : "An unknown error occurred");
            } finally {
                setIsLoading(false);
            }
        },
        [authFetch, isReady]
    );

    const resetWaitingUpdate = useCallback(() => {
        setWaitingUpdate(null);
        setError("");
    }, []);

    return { waitingUpdate, isLoading, error, getPersonWaitingUpdate, resetWaitingUpdate };
};

export default useGetPersonWaitingUpdate;
